'use client';

import { useEffect, useState, useCallback } from 'react';
import { useAuthStore, type AuthUser } from '@/stores/auth-store';
import { authApi, toAuthUser } from '@/lib/api/auth';

interface AuthHydrationResult {
  isHydrated: boolean;
  isLoadingUser: boolean;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshUser: () => Promise<AuthUser | null>;
}

/**
 * Hook to handle auth hydration.
 * 1. Restores tokens from cookies
 * 2. Fetches fresh user data from /auth/me
 * Never reads user from localStorage.
 */
export function useAuthHydration(): AuthHydrationResult {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);

  const setUser = useAuthStore((state) => state.setUser);
  const clear = useAuthStore((state) => state.clear);

  const refreshUser = useCallback(async (): Promise<AuthUser | null> => {
    const token = useAuthStore.getState().accessToken;
    if (!token) return null;

    try {
      setIsLoadingUser(true);
      const freshUser = await authApi.me();
      const authUser = toAuthUser(freshUser);
      setUser(authUser);
      return authUser;
    } catch {
      clear();
      return null;
    } finally {
      setIsLoadingUser(false);
    }
  }, [setUser, clear]);

  // Step 1: hydrate tokens from cookies, then fetch user from API
  useEffect(() => {
    const hydrate = async () => {
      // Wait for zustand persist to finish
      if (!useAuthStore.persist.hasHydrated()) {
        await new Promise<void>((resolve) => {
          useAuthStore.persist.onFinishHydration(() => resolve());
        });
      }

      // Restore tokens from cookies
      useAuthStore.getState().hydrateFromCookies();

      // If we have a token, fetch user from API
      const token = useAuthStore.getState().accessToken;
      if (token) {
        await refreshUser();
      }

      setIsHydrated(true);
    };

    hydrate();
  }, [refreshUser]);

  const accessToken = useAuthStore((state) => state.accessToken);
  const isAuthenticated = !!accessToken;

  return { isHydrated, isLoadingUser, isAuthenticated, accessToken, refreshUser };
}
